import { useLocalization } from "@fluent/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Row, useBreakpoints } from "@narmi/design_system";
import { default as ByzantineAddress } from "byzantine/src/Address";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { BackButton } from "@lending/components/BackButton/BackButton";
import { PageContent } from "@lending/components/PageContent";
import { PageHeader } from "@lending/components/PageHeader";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { useListApplicationApplicantDetailsQuery } from "@lending/hooks/queries/ApplicationApplicantDetails/useListApplicationApplicantDetailsQuery";
import { usePutApplicationApplicantDetailsQuery } from "@lending/hooks/queries/ApplicationApplicantDetails/usePutApplicationApplicantDetailsQuery";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { useToastsContext } from "@lending/hooks/useToasts";
import { useFlowNavigation } from "@lending/navigation";
import { AddressFormData } from "@lending/types";
import { findPrimaryApplicant } from "@lending/utils/applicantHelpers";
import { AddressFormFields } from "./AddressFormFields";
import { buildMailingAddress, createDefaultValues, emptyAddress } from "./helpers";
import { applyAddressErrors, createAddressSchema } from "./shared";

function Address() {
  const { goNext, startOver, goBack } = useFlowNavigation();
  const { l10n } = useLocalization();
  const { showErrorToast, showGenericErrorToast } = useToastsContext();
  const { s } = useBreakpoints();
  const { institution } = useGetInstitutionQuery();
  const askMailingAddress = Boolean(institution.lending_ask_mailing_address);
  const requiredMessage = l10n.getString("error-required", null, "Required");
  const schema = createAddressSchema(l10n, askMailingAddress);

  const { data } = useListApplicationApplicantDetailsQuery();
  const applicant = findPrimaryApplicant(data?.results);

  const { mutateAsync: updateApplicationApplicantDetails, isPending } =
    usePutApplicationApplicantDetailsQuery({
      onSuccess: () => {
        goNext();
      },
      onError: (error) => {
        const { hasFieldErrors, hasToastErrors } = applyAddressErrors(
          error,
          setError,
          showErrorToast,
          requiredMessage
        );
        if (!hasFieldErrors && !hasToastErrors) {
          showGenericErrorToast();
        }
      },
    });

  const { handleSubmit, control, setValue, setError, watch, reset } = useForm<AddressFormData>({
    defaultValues: {
      residential_address: emptyAddress(),
      mailing_address: emptyAddress(),
      hasSameMailingAddress: true,
    },
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (applicant) {
      reset(createDefaultValues(applicant));
    }
  }, [applicant, reset]);

  const hasSameMailingAddress = watch("hasSameMailingAddress");

  const onUpdate = (address: ByzantineAddress) => {
    setValue("residential_address", address);
  };

  const onMailingAddressUpdate = (address: ByzantineAddress) => {
    setValue("mailing_address", address);
  };

  async function onSubmit(formData: AddressFormData) {
    if (!formData.residential_address) {
      return;
    }

    if (!applicant) {
      showGenericErrorToast();
      startOver();
      return;
    }

    await updateApplicationApplicantDetails({
      params: {
        path: {
          uuid: applicant.id,
        },
      },
      body: {
        ...applicant,
        residential_address: { ...formData.residential_address, country_code: "US" },
        mailing_address: buildMailingAddress(
          formData,
          applicant.mailing_address,
          askMailingAddress
        ),
      },
    });
  }

  return (
    <>
      <PageLayout>
        <PageContent>
          <PageHeader
            heading={l10n.getString("address-heading", null, "What's your home address?")}
            subheading={l10n.getString(
              "address-subheading",
              null,
              "We need your current address to confirm your eligibility."
            )}
          />
          <AddressFormFields
            askMailingAddress={askMailingAddress}
            control={control}
            hasSameMailingAddress={hasSameMailingAddress}
            isBreakpoint={s}
            l10n={l10n}
            onMailingAddressUpdate={onMailingAddressUpdate}
            onResidentialAddressUpdate={onUpdate}
          />
          <Row alignItems="center" justifyContent="space-between">
            <BackButton onClick={goBack} />
            <Button
              label={l10n.getString("button-label-next", null, "Next")}
              onClick={handleSubmit(onSubmit)}
              isLoading={isPending}
            />
          </Row>
        </PageContent>
      </PageLayout>
    </>
  );
}

export { Address };
